import { scaleVectorFill, type TextPathParams, type VectorFillData } from '@lite-workspace/dto-fotoclass-templates';
import { frameDelta, type FrameRect } from '../geometry/frame-delta';
import { PATH_NUMBER } from './path-number';
import { arcFractionUnderScale } from './path-sampler';
import { type TextAnchor, type TextTransformMatrix } from '../interfaces/text-params.interface';

const PATH_NUMBERS = new RegExp(PATH_NUMBER, 'g');

// SVG d-string: x/y alternate across M/L/C; no canvas-engine import — its barrel drags Angular/Konva into this pure file.
export function scalePathD(d: string, sx: number, sy: number, tx: number, ty = 0): string {
  if (sx === 1 && sy === 1 && tx === 0 && ty === 0) return d;
  let isX = true;
  return d.replace(PATH_NUMBERS, raw => {
    const scaled = Number(raw) * (isX ? sx : sy) + (isX ? tx : ty);
    isX = !isX;
    return String(Math.round(scaled * 100) / 100);
  });
}

// Shape shared by DrawableTextData['text'] and CustomTextTemplateBox.params — anchor/transform/
// textPath bake absolute coords in the same frame as the container/rect they sit on.
export interface TextSatellites {
  readonly anchor?: TextAnchor;
  readonly transform?: TextTransformMatrix;
  readonly textPath?: TextPathParams;
  readonly fillGradient?: VectorFillData;
  readonly fillGradientDisabled?: VectorFillData;
}

/**
 * The one law of a text path under a frame scale: `d` maps every point by (sx·x + dx, sy·y + dy), and the arc-length
 * fractions on it (`pathRangeStart`/`pathRangeEnd`, the legacy `startOffset`) move to the image of the point they
 * marked (`arcFractionUnderScale`). Copied as is, a fraction would land on the same share of the NEW length — a point
 * elsewhere on the curve whenever sx ≠ sy. A uniform scale keeps every fraction as it is; whole laps (0, 1, a closed
 * path's wrap past its start) never move. A non-string `d` (un-normalised legacy shape) passes through untouched.
 */
export function scaleTextPathOnAxes(textPath: TextPathParams, sx: number, sy: number, dx: number, dy = 0): TextPathParams {
  if (typeof textPath.d !== 'string') return textPath;
  const scaled: TextPathParams = { ...textPath, d: scalePathD(textPath.d, sx, sy, dx, dy) };
  const { pathRangeStart, pathRangeEnd, startOffset } = textPath;
  const hasFraction = [pathRangeStart, pathRangeEnd, startOffset].some(f => typeof f === 'number' && !Number.isInteger(f));
  const remap = hasFraction ? arcFractionUnderScale(textPath.d, textPath.closed === true, sx, sy) : null;
  if (!remap) return scaled;
  return {
    ...scaled,
    ...(typeof pathRangeStart === 'number' && { pathRangeStart: remap(pathRangeStart) }),
    ...(typeof pathRangeEnd === 'number' && { pathRangeEnd: remap(pathRangeEnd) }),
    ...(typeof startOffset === 'number' && { startOffset: remap(startOffset) }),
  };
}

/**
 * The one law of a text matrix under a frame scale: the offset `[4..5]` rides the axes like every satellite point,
 * the linear part `[0..3]` stays. The font size carries the glyph size and the renderer divides the matrix by its
 * mean scale, so the linear part holds only the user's rotation and the PSD's anisotropy — scaling it by unequal
 * axes would stretch the glyphs and shear a rotated line. Every resize of a text frame goes through here.
 */
export function scaleTextMatrix(t: TextTransformMatrix, sx: number, sy: number, dx: number, dy = 0): TextTransformMatrix {
  return [t[0], t[1], t[2], t[3], sx * t[4] + dx, sy * t[5] + dy];
}

/**
 * Maps every satellite point p (anchor, transform[4..5], textPath.d pairs) → (sx·p.x + dx, sy·p.y + dy), the path's
 * arc fractions with it (`scaleTextPathOnAxes`);
 * transform[0..3] untouched (`scaleTextMatrix`). Frame change old → new: (sx, 0, 0, sy, dx, dy) = `frameDelta(old, new)`.
 * Construction-time box.params → drawable scale stays in generateDrawableText (per-field math).
 */
export function shiftTextSatellites<T extends TextSatellites>(text: T, dx: number, dy = 0, sx = 1, sy = 1): T {
  if (dx === 0 && dy === 0 && sx === 1 && sy === 1) return text;

  const anchor = text.anchor ? { ...text.anchor, x: sx * text.anchor.x + dx, y: sy * text.anchor.y + dy } : undefined;
  const transform = text.transform ? scaleTextMatrix(text.transform, sx, sy, dx, dy) : undefined;
  const textPath = text.textPath && scaleTextPathOnAxes(text.textPath, sx, sy, dx, dy);

  return { ...text, ...(anchor && { anchor }), ...(transform && { transform }), ...(textPath && { textPath }) };
}

/**
 * The one frame-follow rule of every sink: d, transform and a boxTopLeft anchor map by the same frame delta, so the
 * Konva/SVG `d − anchor` keeps its place on the box. A baseline anchor stays — no renderer reads it after import.
 */
export function textSatellitesForFrame<T extends TextSatellites>(text: T, from: FrameRect, to: FrameRect): T {
  const [sx, , , sy, dx, dy] = frameDelta(from, to);
  return textSatellitesByAxes(text, sx, sy, dx, dy);
}

/**
 * The same rule for a sink that picks its own map p ↦ (sx·p.x + dx, sy·p.y + dy) instead of the frame delta — e.g. a
 * line keeping its shape (sx = sy) while its frame stretches to a band.
 */
export function textSatellitesByAxes<T extends TextSatellites>(text: T, sx: number, sy: number, dx: number, dy: number): T {
  const moved = withFrameFills(shiftTextSatellites(text, dx, dy, sx, sy), sx, sy);
  return moved !== text && text.anchor?.kind === 'baseline' ? { ...moved, anchor: text.anchor } : moved;
}

/**
 * The gradient fill lies in the box frame, like a vector's: a linear axis is rebuilt from the frame's w/h, a radial
 * ramp keeps absolute px, so it rides the frame by the axis ratio alone — no translation (`scaleVectorFill`).
 * Shared by every sink that resizes a text frame, so the ramp can't be left at the old size by one of them.
 */
export function withFrameFills<T extends TextSatellites>(text: T, sx: number, sy: number): T {
  if (sx === 1 && sy === 1) return text;
  const { fillGradient, fillGradientDisabled } = text;
  if (fillGradient?.kind !== 'radialGradient' && fillGradientDisabled?.kind !== 'radialGradient') return text;
  return {
    ...text,
    ...(fillGradient && { fillGradient: scaleVectorFill(fillGradient, sx, sy) }),
    ...(fillGradientDisabled && { fillGradientDisabled: scaleVectorFill(fillGradientDisabled, sx, sy) }),
  };
}

/** A re-measured frame (not a gesture): d and transform stay, a boxTopLeft anchor pins to the new top-left. */
export function pinTextAnchorToFrame<T extends TextSatellites>(text: T, to: FrameRect): T {
  return text.anchor?.kind === 'boxTopLeft' ? { ...text, anchor: { kind: 'boxTopLeft', x: to.x, y: to.y } } : text;
}
